'use client';

import { isWebView, sendPlatformDataToApp } from '@/app/components/hotel-detail/lib/appNativeTools';
import { differenceInDays, parse } from 'date-fns';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import type { StaticData } from '@/app/common/createStaticData.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { flightClassAvailabilityTranslations } from '@/app/components/hotel-detail/flight-train/TransportJourneySummary';
import type { SearchInfoDetailData } from '@/app/search/types.ts';
import { DESTINATION_TYPE } from '@/app/search/types.ts';
import type { InitialParams } from './InitialParamsProvider';
import type { UrlSearchParamsContextProps } from './UrlSearchParamsProvider';
import type { HotelStaticData } from './common/staticHotelDataAdapter';
import { basicFlightDateFormat, getNights, getTripNights } from './common/dates';

function parseSubCategory(subCategory: string) {
  return subCategory.replace(/_/g, ' ');
}

type CreateProductAccommodationBaseProps = {
  hotelStaticData?: HotelStaticData | null;
  initialParams: InitialParams;
  urlSearchParams: UrlSearchParamsContextProps;
};
const createProductAccommodationBase = ({
  hotelStaticData,
  initialParams,
  urlSearchParams,
}: CreateProductAccommodationBaseProps) => ({
  class: hotelStaticData?.rating?.toString() || '',
  destinationCode: hotelStaticData?.cityId || '',
  destinationName: hotelStaticData?.city || '',
  internalId: initialParams.internalHotelId,
  name: hotelStaticData?.name || '',
  providerName: '', // REV currently it is not sent properly
  quantity: urlSearchParams.totalRooms,
  isTopSecret: hotelStaticData?.isTopSecret || false,
  withPhoto: (hotelStaticData?.images?.length ?? 0) > 0,
  rating: hotelStaticData?.hotelOpinion.rating || 0,
  reviewsNumber: hotelStaticData?.hotelOpinion.numberOfReviews || 0,
  destinationCountryCode: hotelStaticData?.countryId || '',
  destinationCountryName: hotelStaticData?.countryName || '',
});

const createSearchDestinationCode = (searchInfoDetailData: SearchInfoDetailData) =>
  searchInfoDetailData.destination.destinationType === DESTINATION_TYPE.CITY
    ? searchInfoDetailData.destination.geodbId
    : searchInfoDetailData.destination.id;

type TrackVisitEventErrorParams = {
  errorType:
    | 'ERROR_SEARCH_RESPONSE'
    | 'ERROR_ROOMS_RESPONSE'
    | 'ERROR_TRACKING_INFO_RESPONSE'
    | 'ERROR_FETCHING_ROOMS'
    | 'PAST_DATES';
  initialParams: InitialParams;
  urlSearchParams: UrlSearchParamsContextProps;
  hotelStaticData?: HotelStaticData | null;
  hotelPromotions?: PromotionSuccess | null;
  pricingId: string;
  pageSection: string;
  staticData: StaticData;
  commonData: CommonData;
  localization: Localization;
  searchInfoDetailData: SearchInfoDetailData;
};

export function trackEventError({
  errorType,
  initialParams,
  hotelStaticData,
  hotelPromotions,
  pricingId,
  urlSearchParams,
  pageSection,
  staticData,
  commonData,
  localization,
  searchInfoDetailData,
}: TrackVisitEventErrorParams) {
  const { categoryLevelCode } = localization;

  const platformDataPayload = {
    event: 'visit',
    error: errorType,
    page: {
      brand: staticData.domainValues?.coreBrand,
      category: urlSearchParams.accommodationOnly ? 'hotel' : 'dp',
      categoryLevelCode,
      locale: localization.locale.toLowerCase().replace('-', '_'),
      platformName: 'TSX',
      platformVersion: process.env.NEXT_PUBLIC_APP_VERSION,
      section: pageSection,
      type: 'bkFlow',
      ...(localization.isWhiteLabel && localization.whiteLabelName
        ? { whitelabelName: localization.whiteLabelName.toLowerCase() }
        : {}),
    },
    product: {
      offerType: hotelPromotions?.promotionType,
      transactionAffiliation: localization.businessProfile,
      accomodation: createProductAccommodationBase({ hotelStaticData, initialParams, urlSearchParams }),
      adults: urlSearchParams.adults,
      children: urlSearchParams.children,
      infants: urlSearchParams.infants,
      numberOfGuests: urlSearchParams.totalGuests,
      ...(!urlSearchParams.accommodationOnly ? { trip: { roundTrip: true } } : undefined),
      category: urlSearchParams.accommodationOnly ? 'hotel' : urlSearchParams.isTrain ? 'trainHotel' : 'flightHotel',
      ...(pricingId ? { pricingId } : {}),
    },
    search: {
      hdpSearchId: initialParams.hdpSearchId,
      destinationCode: createSearchDestinationCode(searchInfoDetailData) || '',
    },
    user: {
      currency: localization.currency,
      sessionId: commonData.sessionId,
      uid: commonData.userUid,
      deviceType: commonData.deviceType,
      dq: commonData.casperResponseProperties?.dq,
    },
  };

  if (canUseDOM) {
    window.platformData?.push(platformDataPayload);
  }

  if (isWebView) sendPlatformDataToApp(platformDataPayload);
}

type TrackVisitEventParams = {
  mealPlan: MealPlanType | undefined;
  roomName: string;
  // Uplift of the transport this section is about, in the same unit as `mealPlan.finalPrice`
  priceIncrement?: number;
  checkin: string;
  checkout: string;
  hotelStaticData: HotelStaticData | null | undefined;
  initialParams: InitialParams;
  promotionType: string;
  urlSearchParams: UrlSearchParamsContextProps;
  pricingId: string | undefined;
  pageSection: string;
  staticData: StaticData;
  commonData: CommonData;
  localization: Localization;
  t: TranslationFunction;
  selectedFlight?: Pick<TransportsType, 'inbound' | 'outbound'>;
  searchInfoDetailData: SearchInfoDetailData;
};

export function trackVisitEvent({
  mealPlan,
  roomName,
  priceIncrement = 0,
  checkin,
  checkout,
  hotelStaticData,
  initialParams,
  promotionType,
  urlSearchParams,
  pricingId,
  selectedFlight,
  pageSection,
  staticData,
  commonData,
  localization,
  searchInfoDetailData,
  t,
}: TrackVisitEventParams) {
  const nights = checkin && checkout ? getNights(checkin, checkout) : urlSearchParams.nights;
  const providerId = mealPlan?.supplier.id || 0;
  // Both totals hang off the same meal plan, so the uplift can follow the rate the server applied
  // rather than leaving the two currencies disagreeing by it. No rate means no uplift in either.
  const eurRate = mealPlan?.finalTotalPrice ? mealPlan.finalTotalPriceEUR / mealPlan.finalTotalPrice : 0;
  // Only a PER_PERSON increment is per-traveller: a TOTAL / PER_ROOM_NIGHT one already covers the whole
  // party, so it must not be scaled (see usePriceDisplay). A non-finite pax count drops the uplift
  // rather than reporting it unscaled, which would be a wrong figure rather than a missing one.
  const pax = urlSearchParams.totalGuestsWithoutInfants;
  const paxScale = initialParams.priceType !== 'PER_PERSON' ? 1 : Number.isFinite(pax) ? pax : 0;
  const priceUplift = eurRate ? priceIncrement * paxScale : 0;
  const totalPriceEuro = Math.trunc((mealPlan?.finalTotalPriceEUR || 0) + priceUplift * eurRate);
  const totalPrice = Math.round(((mealPlan?.finalTotalPrice || 0) + priceUplift) * 100) / 100;
  const platformDataPayload = {
    transactionAffiliation: localization.businessProfile,
    event: 'visit',
    page: {
      brand: staticData.domainValues?.coreBrand || 'lastminute',
      category: urlSearchParams.accommodationOnly ? 'hotel' : 'dp',
      categoryLevelCode: localization.categoryLevelCode,
      locale: localization.locale.toLowerCase().replace('-', '_'),
      platformName: 'TSX',
      platformVersion: process.env.NEXT_PUBLIC_APP_VERSION || '',
      section: pageSection,
      subCategory: parseSubCategory(
        hotelStaticData?.accommodationType
          ?.split('_')
          .map((str) => str.charAt(0).toUpperCase() + str.slice(1))
          .join('_') || ''
      ),
      type: 'bkFlow',
      ...(localization.isWhiteLabel && localization.whiteLabelName
        ? { whitelabelName: localization.whiteLabelName.toLowerCase() }
        : {}),
    },
    user: {
      currency: localization.currency,
      sessionId: commonData.sessionId || '',
      uid: commonData.userUid || '',
      deviceType: commonData.deviceType,
      dq: commonData.casperResponseProperties?.dq,
      isBot: commonData.isBot,
    },

    product: {
      accomodation: {
        ...createProductAccommodationBase({ hotelStaticData, initialParams, urlSearchParams }),
        duration: nights,
        endDate: checkout,
        mealPlan: mealPlan?.mealPlanName || '',
        providerId,
        startDate: checkin,
        type: roomName,
      },
      offerType: promotionType,
      adults: urlSearchParams.adults,
      advancedBooking: differenceInDays(parse(checkin, 'yyyyMMdd', new Date()), new Date()),
      category: urlSearchParams.accommodationOnly ? 'hotel' : urlSearchParams.isTrain ? 'trainHotel' : 'flightHotel',
      children: urlSearchParams.children,
      infants: urlSearchParams.infants,
      name: `${providerId}|${hotelStaticData?.name}`,
      numberOfGuests: urlSearchParams.totalGuests,
      priceEUR: totalPriceEuro,
      priceUserCurrency: totalPrice,
      sku: `${providerId}|${initialParams.internalHotelId}`,
      ...(pricingId ? { pricingId } : {}),
      ...(selectedFlight
        ? {
            trip: {
              duration: getTripNights(
                selectedFlight.outbound.departureDate || '',
                selectedFlight?.inbound.arrivalDate || ''
              ),
              name: [...(selectedFlight?.outbound.segments || []), ...(selectedFlight?.inbound.segments || [])]
                .map((segment) => segment.transportNumber || '')
                .join('|'),
              departureDate: basicFlightDateFormat(selectedFlight.outbound.departureDate, 'yyyyMMdd'),
              outboundDepartureWeekDay: '', // REV missing field
              outboundDestinationCode: selectedFlight.outbound.destination,
              outboundDestinationName: null,
              outboundOriginCode: selectedFlight.outbound.origin,
              outboundOriginMacroCode: searchInfoDetailData?.origin?.cityId || '',
              outboundOriginName: null,
              outboundProviderId: selectedFlight.outbound.segments[0]?.company.code || '',
              outboundProviderName: selectedFlight.outbound.segments[0]?.company.name || '',
              outboundArrivalHour: selectedFlight.outbound.arrivalDate.split('#')[1].split(':').join(''),
              outboundDepartureHour: selectedFlight.outbound.departureDate.split('#')[1].split(':').join(''),
              outboundDuration: '', // REV missing field
              outboundStops: `${selectedFlight.outbound.stops + 1}`,
              returnDate: basicFlightDateFormat(selectedFlight.inbound.departureDate, 'yyyyMMdd'),
              returnDepartureWeekDay: '', // REV missing field
              returnDestinationCode: selectedFlight.inbound.destination,
              returnDestinationName: null,
              returnOriginCode: selectedFlight.inbound.origin,
              returnOriginName: null,
              returnProviderId: selectedFlight.inbound.segments[0]?.company.code || '',
              returnProviderName: selectedFlight.inbound.segments[0]?.company.name || '',
              returnArrivalHour: selectedFlight.inbound.arrivalDate.split('#')[1].split(':').join(''),
              returnDepartureHour: selectedFlight.inbound.departureDate.split('#')[1].split(':').join(''),
              returnDuration: '', // REV missing field
              returnStops: `${selectedFlight.inbound.stops + 1}`,
              round: true,
              class: t(
                flightClassAvailabilityTranslations[selectedFlight?.outbound.segments[0]?.classAvailability || 'Y']
              ),
            },
          }
        : undefined),
    },
    search: {
      hdpSearchId: initialParams.hdpSearchId,
      destinationCode: createSearchDestinationCode(searchInfoDetailData) || '',
    },
    results: {
      idRequest: 0, // REV not provided by Travel API
    },
  };

  if (canUseDOM) {
    window.platformData?.push(platformDataPayload);
  }

  if (isWebView) sendPlatformDataToApp(platformDataPayload);
}
